import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { injectFavicon, resetFavicon } from '../composables/useFavicon';

function hexToRgb(hex) {
  var h = hex.replace('#', '');
  return parseInt(h.substring(0,2), 16) + ' ' + parseInt(h.substring(2,4), 16) + ' ' + parseInt(h.substring(4,6), 16);
}

// ---- Brand shade-ramp generation ----
// tailwind.config.js's `brand` scale reads --wl-brand-{shade} CSS vars (see
// style.css for the default ramp, identical to the old hardcoded hex). To
// white-label brand-* everywhere without washing out the light tints (a
// naive opacity-blend of a saturated color over white looks muddy), we
// generate a proper HSL lightness ramp from the tenant's single primary hex.
function hexToHsl(hex) {
  var r = parseInt(hex.substring(1, 3), 16) / 255;
  var g = parseInt(hex.substring(3, 5), 16) / 255;
  var b = parseInt(hex.substring(5, 7), 16) / 255;
  var max = Math.max(r, g, b), min = Math.min(r, g, b);
  var h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgbStr(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  var r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return Math.round(r * 255) + ' ' + Math.round(g * 255) + ' ' + Math.round(b * 255);
}

// Lightness target per shade — the tenant's chosen color lands at ~600,
// the most-used shade (buttons, links), with tints/shades generated around it.
var BRAND_SHADE_LIGHTNESS = { 50: 96, 100: 91, 200: 82, 300: 70, 400: 58, 500: 50, 600: 44, 700: 37, 800: 30, 900: 24, 950: 15 };
var BRAND_SHADES = Object.keys(BRAND_SHADE_LIGHTNESS);

function brandRampFromHex(hex) {
  var hsl = hexToHsl(hex);
  var ramp = {};
  BRAND_SHADES.forEach(function (shade) {
    ramp[shade] = hslToRgbStr(hsl[0], hsl[1], BRAND_SHADE_LIGHTNESS[shade]);
  });
  return ramp;
}

function getWLStyleId() { return '__wl_override_style'; }

function injectCSSVariables(config) {
  var root = document.documentElement;
  var p = config.primary_color || '#2563EB';
  var a = config.accent_color || '#F59E0B';
  var ar = hexToRgb(a);
  root.style.setProperty('--wl-font', config.font_family || 'Inter');
  // Note: .white-label-active class is added by the ReportPage component, not body

  var sid = getWLStyleId();
  var el = document.getElementById(sid);
  if (!el) { el = document.createElement('style'); el.id = sid; document.head.appendChild(el); }

  var sitewide = !!(config.settings_json && config.settings_json.apply_sitewide);
  var swPrimary = sitewide ? (config.settings_json.sitewide_primary || p) : null;
  var swAccent = sitewide ? (config.settings_json.sitewide_accent || a) : null;
  var swar = swAccent ? hexToRgb(swAccent) : null;

  var rules = [];

  // ---- Brand: real CSS custom properties (tailwind.config.js reads them
  // directly), so every brand-* shade — including light tints — just works,
  // with no per-utility-class !important overrides needed.
  if (sitewide) {
    // Sitewide opt-in: set on :root so brand-* is tenant-colored everywhere.
    var swRamp = brandRampFromHex(swPrimary);
    BRAND_SHADES.forEach(function (shade) {
      root.style.setProperty('--wl-brand-' + shade, swRamp[shade]);
    });
  } else {
    // Default: leave :root at the stylesheet's baseline ramp, and scope the
    // tenant's ramp to .white-label-active (added only by ReportPage.vue)
    // so branding shows on shared reports without recoloring the whole app.
    BRAND_SHADES.forEach(function (shade) { root.style.removeProperty('--wl-brand-' + shade); });
    var ramp = brandRampFromHex(p);
    var vars = BRAND_SHADES.map(function (shade) { return '--wl-brand-' + shade + ':' + ramp[shade] + ';'; }).join('');
    rules.push('.white-label-active{' + vars + '}');
  }

  // ---- Accent: kept as the fixed "success/positive" semantic color,
  // deliberately NOT wired to CSS vars (see tailwind.config.js) — a
  // tenant's accent could be any hue, and that shouldn't repaint every
  // "passed" checkmark and success state across the report. Still themed
  // via the existing scoped/sitewide !important overrides below.
  var S = '.white-label-active ';
  rules.push(
    S+'.bg-accent-100{background-color:rgba('+ar+',0.15)!important}',
    S+'.bg-accent-50{background-color:rgba('+ar+',0.1)!important}',
    S+'.bg-accent-500,'+S+'.bg-accent-500\\/20,'+S+'.badge-accent{background-color:rgb('+ar+')!important}',
    S+'.border-accent-500{border-color:rgb('+ar+')!important}'
  );
  if (sitewide) {
    rules.push(
      '.bg-accent-100{background-color:rgba('+swar+',0.15)!important}',
      '.bg-accent-50{background-color:rgba('+swar+',0.1)!important}',
      '.bg-accent-500,.bg-accent-500\\/20,.badge-accent{background-color:rgb('+swar+')!important}',
      '.border-accent-500{border-color:rgb('+swar+')!important}'
    );
  }
  el.textContent = rules.join('\n');

  // Real gap reported live 2026-09-10 -- see composables/useFavicon.js's
  // own docstring for the full writeup ("Custom logo, favicon, and brand
  // colors" was promised on /features but never actually implemented).
  injectFavicon(config.logo_url);
}

function resetCSSVariables() {
  var root = document.documentElement;
  root.style.removeProperty('--wl-font');
  BRAND_SHADES.forEach(function (shade) { root.style.removeProperty('--wl-brand-' + shade); });
  // Remove injected style override
  var el = document.getElementById(getWLStyleId());
  if (el) el.remove();
  resetFavicon();
}

function getErrText(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || String(err);
  if (err.message) return String(err.message);
  if (err.detail) {
    var d = err.detail;
    if (Array.isArray(d)) return d.map(function(e) { return e.msg || String(e); }).join('; ');
    return String(d);
  }
  try { return JSON.stringify(err); } catch(e) { return String(err); }
}

async function fetchErrText(response) {
  try {
    var data = await response.json();
    var d = data.detail;
    if (d) {
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) return d.map(function(e) { return (e.msg || String(e)); }).join('; ');
      return String(d);
    }
    return data.error || data.message || ('HTTP ' + response.status);
  } catch(e) {
    return 'HTTP ' + response.status;
  }
}

export var useWhiteLabelStore = defineStore('whiteLabel', function() {
  var config = ref(null);
  var loading = ref(false);
  var error = ref(null);
  var tierInfo = ref(null);
  var tierInfoLoading = ref(false);
  // Real bug reported live 2026-09-10 (owner: "if I save the page White-
  // Label Branding and refresh the page after all saved settings are
  // gone"): App.vue's onMounted calls whiteLabelStore.init() (which calls
  // fetchConfig() unconditionally) on every authenticated page load,
  // completely independent of WhiteLabelSettings.vue's OWN onMounted guard
  // (`if (!wlStore.config && !wlStore.loading) fetchConfig()`) -- so a hard
  // refresh on this page fires TWO concurrent GET /api/whitelabel/config
  // requests. Whichever resolves LAST wins (overwrites config.value), with
  // no ordering guarantee -- if either fails or a transient hiccup returns
  // an incomplete response, the good, already-loaded data could be
  // clobbered by the other. Tracking the in-flight request here and
  // returning the SAME promise to a second, overlapping caller makes this
  // structurally impossible instead of merely unlikely.
  var _fetchConfigInFlight = null;

  var isActive = computed(function() { return config.value && config.value.is_active ? true : false; });
  var companyName = computed(function() { return (config.value && config.value.is_active && config.value.company_name) || ''; });
  var logoUrl = computed(function() { return (config.value && config.value.logo_path) ? '/static/' + config.value.logo_path : ''; });
  var colors = computed(function() { return { primary: (config.value && config.value.primary_color) || '#2563EB', secondary: (config.value && config.value.secondary_color) || '#1E40AF', accent: (config.value && config.value.accent_color) || '#F59E0B' }; });
  var isTierAllowed = computed(function() { return tierInfo.value && tierInfo.value.is_allowed ? true : false; });
  var tierFeatures = computed(function() { return (tierInfo.value && tierInfo.value.features) || {}; });

  function fetchConfig() {
    // Dedup concurrent callers (App.vue's whiteLabelStore.init() and this
    // page's own onMounted guard both fire on a hard refresh) -- see the
    // note on _fetchConfigInFlight above for the real bug this closes.
    if (_fetchConfigInFlight) return _fetchConfigInFlight;
    _fetchConfigInFlight = _doFetchConfig().finally(function() { _fetchConfigInFlight = null; });
    return _fetchConfigInFlight;
  }

  async function _doFetchConfig() {
    loading.value = true; error.value = null;
    try {
      var resp = await fetch('/api/whitelabel/config', { credentials: 'include', headers: { Accept: 'application/json' } });
      if (resp.status === 404) { config.value = null; resetCSSVariables(); return null; }
      if (!resp.ok) { error.value = await fetchErrText(resp); return null; }
      var data = await resp.json();
      config.value = data.config || data;
      if (config.value && config.value.is_active) {
        injectCSSVariables(config.value);
      } else {
        resetCSSVariables();
      }
      return config.value;
    } catch(e) { error.value = getErrText(e); console.error('[WL] fetchConfig:', getErrText(e)); return null; }
    finally { loading.value = false; }
  }

  async function saveConfig(payload) {
    loading.value = true; error.value = null;
    var resp = await fetch('/api/whitelabel/config', { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; loading.value = false; throw new Error(msg); }
    var data = await resp.json();
    config.value = data.config || data;
    if (config.value && config.value.is_active) {
      injectCSSVariables(config.value);
    } else {
      resetCSSVariables();
    }
    loading.value = false;
    return config.value;
  }

  async function deleteConfig() {
    loading.value = true; error.value = null;
    try {
      var resp = await fetch('/api/whitelabel/config', { method: 'DELETE', credentials: 'include', headers: { Accept: 'application/json' } });
      if (!resp.ok) { error.value = await fetchErrText(resp); return; }
      config.value = null; resetCSSVariables();
    } catch(e) { error.value = getErrText(e); }
    finally { loading.value = false; }
  }

  async function uploadLogo(file) {
    var fd = new FormData();
    fd.append('file', file);
    loading.value = true; error.value = null;
    var resp = await fetch('/api/whitelabel/logo', { method: 'POST', credentials: 'include', body: fd });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; loading.value = false; throw new Error(msg); }
    loading.value = false;
    await fetchConfig();
    return true;
  }

  async function removeLogo() {
    var resp = await fetch('/api/whitelabel/logo', { method: 'DELETE', credentials: 'include' });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; throw new Error(msg); }
    await fetchConfig();
  }

  async function uploadScreenshot(file) {
    var fd = new FormData();
    fd.append('file', file);
    loading.value = true; error.value = null;
    var resp = await fetch('/api/whitelabel/screenshot', { method: 'POST', credentials: 'include', body: fd });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; loading.value = false; throw new Error(msg); }
    loading.value = false;
    var data = await resp.json();
    await fetchConfig();
    return data.screenshot_url;
  }

  async function removeScreenshot() {
    var resp = await fetch('/api/whitelabel/screenshot', { method: 'DELETE', credentials: 'include' });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; throw new Error(msg); }
    await fetchConfig();
  }

  async function fetchTierInfo() {
    tierInfoLoading.value = true;
    try {
      var resp = await fetch('/api/whitelabel/tier-info', { credentials: 'include', headers: { Accept: 'application/json' } });
      if (resp.ok) tierInfo.value = await resp.json();
    } catch(e) {}
    finally { tierInfoLoading.value = false; }
  }

  async function init() { await Promise.all([fetchConfig(), fetchTierInfo()]); }
  function reset() { config.value = null; tierInfo.value = null; error.value = null; resetCSSVariables(); }

  async function uploadHeaderBgImage(file) {
    var fd = new FormData();
    fd.append('file', file);
    loading.value = true; error.value = null;
    var resp = await fetch('/api/whitelabel/header-bg-image', { method: 'POST', credentials: 'include', body: fd });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; loading.value = false; throw new Error(msg); }
    loading.value = false;
    var data = await resp.json();
    await fetchConfig();
    return data.header_bg_image;
  }

  async function removeHeaderBgImage() {
    var resp = await fetch('/api/whitelabel/header-bg-image', { method: 'DELETE', credentials: 'include' });
    if (!resp.ok) { var msg = await fetchErrText(resp); error.value = msg; throw new Error(msg); }
    await fetchConfig();
  }

  return { config, loading, error, tierInfo, tierInfoLoading, isActive, companyName, logoUrl, colors, isTierAllowed, tierFeatures, fetchConfig, saveConfig, deleteConfig, uploadLogo, removeLogo, uploadScreenshot, removeScreenshot, uploadHeaderBgImage, removeHeaderBgImage, fetchTierInfo, init, reset };
});
